// import { createSlice } from '@reduxjs/toolkit';

// // 🔹 Define version for future state migrations
// const SLICE_VERSION = '1.0';

// // 🔹 Export initial state for versioned state restoration
// export const initialState = {
//   _version: SLICE_VERSION,
//   defaultTabs: [
//     { id: 'my-invoices', name: 'My Invoices' },
//     { id: 'charges-audit', name: 'Charges Audit' },
//     { id: 'estimated-charges', name: 'Estimated Charges' },
//     { id: 'templates-invoices', name: 'Templates' },
//     { id: 'drafts-invoices', name: 'Drafts' },
//   ],
//   dynamicTabs: [],
//   activeTab: 0,
//   auditRefreshKey: 0,
// };

// // 🔹 Slice definition
// const invoicesSlice = createSlice({
//   name: 'invoices',
//   initialState,
//   reducers: {
//     addTab: (state, action) => {
//       const { id, name, type, data } = action.payload || {};
//       const dynamicId =
//         id ||
//         (window.crypto?.randomUUID?.() ??
//           `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`);

//       const tabName = name || `New Invoice ${state.dynamicTabs.length + 1}`;
//       const finalId = dynamicId.toString().startsWith('dyn-') ? dynamicId : `dyn-${dynamicId}`;

//       state.dynamicTabs.push({
//         id: finalId,
//         name: tabName,
//         type: type || 'new',
//         data: data || null,
//         isDynamic: true,
//         createdAt: Date.now(),
//       });

//       state.activeTab = state.defaultTabs.length + state.dynamicTabs.length - 1;
//     },

//     removeTab: (state, action) => {
//       const { tabIndex } = action.payload;
//       if (tabIndex < state.defaultTabs.length) return;

//       const dynamicIndex = tabIndex - state.defaultTabs.length;
//       state.dynamicTabs.splice(dynamicIndex, 1);

//       state.activeTab = Math.min(
//         state.activeTab,
//         state.defaultTabs.length + state.dynamicTabs.length - 1
//       );
//     },

//     setActiveTab: (state, action) => {
//       const { index } = action.payload;
//       const totalTabs = state.defaultTabs.length + state.dynamicTabs.length;

//       if (index >= 0 && index < totalTabs) {
//         state.activeTab = index;
//       }
//     },

//     resetTabs: (state) => {
//       state.dynamicTabs = [];
//       state.activeTab = 0;
//     },
//     incrementAuditRefresh: state => {
//       state.auditRefreshKey += 1;
//     },
//   },
// });

// // 🔹 Export actions and reducer
// export const { addTab, removeTab, setActiveTab, resetTabs,
//   incrementAuditRefresh } = invoicesSlice.actions;
// export default invoicesSlice.reducer;

import { createSlice } from '@reduxjs/toolkit';
import { pushDynamicTab } from '../utils/dynamicTabs';

// 🔹 Define version for future state migrations
const SLICE_VERSION = '1.0';

// 🔹 Export initial state for versioned state restoration
export const initialState = {
    _version: SLICE_VERSION,
    defaultTabs: [
        { id: 'my-invoices', name: 'My Invoices' },
        { id: 'charges-audit', name: 'Charges Audit' },
        { id: 'estimated-charges', name: 'Estimated Charges' },
        // { id: 'templates-invoices', name: 'Templates' },
        { id: 'drafts-invoices', name: 'Drafts' },
        { id: 'rejected-invoices',      name: 'Rejected' },
    ],
    dynamicTabs: [],
    activeTab: 0,
    auditRefreshKey: 0,
    // ⬇️ keep your local approve/reject overrides here
    chargeAuditOverrides: {},
    // remember pagination/page size per view
    listViewState: {},
};

const invoicesSlice = createSlice({
    name: 'invoices',
    initialState,
    reducers: {
        addTab: (state, action) => {
            const { id, name, type, data, shipments } = action.payload || {};
            const dynamicId =
                id ||
                (window.crypto?.randomUUID?.() ??
                    `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`);

            const tabName = name || `New Invoice ${state.dynamicTabs.length + 1}`;
            const finalId = dynamicId.toString().startsWith('dyn-') ? dynamicId : `dyn-${dynamicId}`;

            pushDynamicTab(
                state,
                {
                    id: finalId,
                    name: tabName,
                    type: type || 'new',
                    data: data || null,
                    shipments: shipments || [],
                    isDynamic: true,
                    createdAt: Date.now(),
                },
                state.defaultTabs.length
            );
        },

        removeTab: (state, action) => {
            const { tabIndex } = action.payload;
            if (tabIndex < state.defaultTabs.length) return;
            const dynamicIndex = tabIndex - state.defaultTabs.length;
            state.dynamicTabs.splice(dynamicIndex, 1);
            state.activeTab = Math.min(
                state.activeTab,
                state.defaultTabs.length + state.dynamicTabs.length - 1
            );
        },

        setActiveTab: (state, action) => {
            const { index } = action.payload;
            const totalTabs = state.defaultTabs.length + state.dynamicTabs.length;
            if (index >= 0 && index < totalTabs) {
                state.activeTab = index;
            }
        },

        resetTabs: (state) => {
            state.dynamicTabs = [];
            state.activeTab = 0;
        },

        incrementAuditRefresh: (state) => {
            state.auditRefreshKey += 1;
        },

        // ⬇️ New: locally override a single charge’s audit status
        setLocalChargeStatus: (state, action) => {
            const { chargeId, status } = action.payload;
            state.chargeAuditOverrides[chargeId] = status;
        },

        setListViewState: (state, action) => {
            const { stateKey, currentPage, itemsPerPage, sortBy, ascending } = action.payload || {};
            if (!stateKey) return;
            state.listViewState = state.listViewState || {};
            state.listViewState[stateKey] = {
                ...(state.listViewState[stateKey] || {}),
                currentPage,
                itemsPerPage,
                sortBy,
                ascending,
            };
        },
    },
});

export const {
    addTab,
    removeTab,
    setActiveTab,
    resetTabs,
    incrementAuditRefresh,
    setLocalChargeStatus,
    setListViewState,
} = invoicesSlice.actions;

export default invoicesSlice.reducer;